// Settings-01b — Accept Invitation page.
//
// Public route ``/invitations/:token``. Hits
// ``GET /api/v1/invitations/{token}`` for a preview, then renders
// one of four states:
//   * ok                — show first/last + brokerage, ask for a
//                         password (or just confirm if the user
//                         already exists)
//   * already_accepted  — friendly success-ish empty state
//   * expired           — friendly empty state w/ "ask your admin"
//   * revoked           — friendly empty state w/ "ask your admin"
//
// On submit POSTs to ``/api/v1/invitations/{token}/accept`` and
// redirects to ``/login`` so the user enters fresh credentials.

import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { AuthShell } from "@/features/auth";
import { Button } from "@/design-system";

import { acceptInvitation, fetchInvitationPreview } from "./api";
import type { InvitationPreview } from "./types";


/**
 * Problem-state card for missing/expired/revoked/etc. invitations.
 * Reuses the centered AuthShell so these states stay visually
 * cohesive with Login + ResetPassword.
 */
function ProblemCard({
  title, body, testId,
}: {
  title: string; body: string; testId: string;
}) {
  return (
    <AuthShell layout="centered" testId={`accept-invitation-shell-${testId}`}>
      <div data-testid={testId} className="flex flex-col gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-text-primary">
          {title}
        </h1>
        <p className="text-sm text-text-secondary">{body}</p>
        <a
          href="/login"
          className="mt-2 inline-flex items-center justify-center rounded-md border border-border-default bg-raised px-3 py-2 text-sm font-medium text-text-primary transition-fast ease-out hover:border-border-hover hover:bg-recessed focus-visible:shadow-focus focus-visible:outline-none"
        >
          Back to sign in
        </a>
      </div>
    </AuthShell>
  );
}


export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const previewQuery = useQuery({
    queryKey: ["invitation-preview", token],
    queryFn: ({ signal }) => fetchInvitationPreview(token!, { signal }),
    enabled: Boolean(token),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 404)
      && failureCount < 1,
  });

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const acceptMutation = useMutation({
    mutationFn: () =>
      acceptInvitation(token!, {
        password: password || undefined,
      }),
    onSuccess: () => {
      navigate("/login", {
        replace: true,
        state: { from: "/dashboard" },
      });
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    acceptMutation.mutate();
  };

  if (!token) {
    return (
      <ProblemCard
        title="Invitation link is incomplete"
        body="The invitation link is missing its token. Ask your administrator to resend the invite."
        testId="accept-invitation-missing-token"
      />
    );
  }

  if (previewQuery.isLoading) {
    return (
      <AuthShell layout="centered" testId="accept-invitation-shell-loading">
        <div
          data-testid="accept-invitation-loading"
          className="flex flex-col items-center gap-2 text-sm text-text-secondary"
        >
          <div
            aria-hidden="true"
            className="h-6 w-6 animate-spin rounded-full border-2 border-border-default border-t-teal"
          />
          Loading invitation…
        </div>
      </AuthShell>
    );
  }

  if (previewQuery.isError) {
    const err = previewQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <ProblemCard
          title="Invitation not found"
          body="The link is invalid or no longer exists. Ask your administrator to send a new invitation."
          testId="accept-invitation-not-found"
        />
      );
    }
    return (
      <ProblemCard
        title="Couldn't load invitation"
        body="Something went wrong on our side. Try the link again in a moment."
        testId="accept-invitation-error"
      />
    );
  }

  const preview = previewQuery.data as InvitationPreview;
  if (preview.status === "already_accepted") {
    return (
      <ProblemCard
        title="Invitation already accepted"
        body="This invitation has already been redeemed. Sign in to continue."
        testId="accept-invitation-already-accepted"
      />
    );
  }
  if (preview.status === "expired") {
    return (
      <ProblemCard
        title="Invitation expired"
        body="This invitation expired. Ask your administrator to resend it."
        testId="accept-invitation-expired"
      />
    );
  }
  if (preview.status === "revoked") {
    return (
      <ProblemCard
        title="Invitation revoked"
        body="An administrator revoked this invitation. Reach out if you believe this is a mistake."
        testId="accept-invitation-revoked"
      />
    );
  }

  // status === "ok"
  const greetingName = preview.first_name || preview.email.split("@")[0];
  const passwordsMatch =
    password.length > 0 && password === confirmPassword;
  const newUserSubmittable =
    !preview.has_existing_user && passwordsMatch;
  const submittable =
    preview.has_existing_user || newUserSubmittable;

  // Split layout for the welcoming OK state — left rail introduces
  // the brokerage context, right side hosts the form.
  const sideContent = (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
          You&rsquo;re invited
        </span>
        <h2
          data-testid="accept-invitation-side-brokerage"
          className="text-lg font-semibold tracking-tight text-text-primary"
        >
          {preview.entity_name}
        </h2>
        <p className="text-sm text-text-secondary">
          Your administrator added you to this brokerage on
          BeneCRM. Finish setting up your account to start
          collaborating with your team.
        </p>
      </div>
      <ul className="flex flex-col gap-2 text-sm text-text-secondary">
        <li className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal"
          />
          <span>Track client renewals and compliance commitments</span>
        </li>
        <li className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal"
          />
          <span>Collaborate with producers and account managers</span>
        </li>
        <li className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal"
          />
          <span>Stay ahead of ACA, ERISA, COBRA &amp; HIPAA deadlines</span>
        </li>
      </ul>
    </>
  );

  return (
    <AuthShell
      layout="split"
      side={sideContent}
      testId="accept-invitation-shell-ok"
    >
      <div data-testid="accept-invitation-ok" className="flex flex-col gap-5">
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-text-primary">
            Welcome, {greetingName}
          </h1>
          <p className="text-sm text-text-secondary">
            Accepting your invitation to{" "}
            <strong className="font-medium text-text-primary">
              {preview.entity_name}
            </strong>{" "}
            on BeneCRM.
          </p>
        </header>

        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <label className="block">
            <span className="block text-sm font-medium text-text-primary">
              Email
            </span>
            <input
              type="email"
              readOnly
              disabled
              value={preview.email}
              data-testid="accept-invitation-email"
              className="mt-1 w-full rounded-md border border-border-default bg-recessed px-3 py-2 text-base text-text-secondary"
            />
          </label>

          {!preview.has_existing_user && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-text-primary">
                  Password
                </span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  data-testid="accept-invitation-password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-text-primary">
                  Confirm password
                </span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  data-testid="accept-invitation-confirm"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
                />
              </label>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-ember">
                  Passwords don&rsquo;t match.
                </p>
              )}
            </>
          )}

          {acceptMutation.error instanceof ApiError && (
            <div
              role="alert"
              data-testid="accept-invitation-submit-error"
              className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember"
            >
              {acceptMutation.error.message}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            loading={acceptMutation.isPending}
            disabled={!submittable}
            data-testid="accept-invitation-submit"
            className="w-full"
          >
            {preview.has_existing_user
              ? "Join brokerage"
              : "Create account & join"}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}
